import { useQuery } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Spinner from '../ui/Spinner';
import { StatusBadge } from '../requests/RequestFilters';
import { passportHistory } from '../../api/passportRegister';

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

export const PASSPORT_STATUS = {
  not_registered: { label: 'No passport record', tone: 'gray', button: 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100' },
  collected: { label: 'With company', tone: 'green', button: 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100' },
  given: { label: 'With candidate', tone: 'purple', button: 'border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100' },
};

export default function PassportHistoryModal({ candidate, onClose }) {
  const query = useQuery({
    queryKey: ['passport-history', candidate?.id],
    queryFn: () => passportHistory(candidate.id),
    enabled: !!candidate,
  });
  const info = candidate ? PASSPORT_STATUS[query.data?.candidate.passport_status || candidate.passport_status] : null;
  const movements = query.data?.movements || [];

  return (
    <Modal
      open={!!candidate}
      onClose={onClose}
      title={candidate ? `Passport history — ${candidate.full_name}` : ''}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      {!candidate ? null : query.isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={info.tone}>{info.label}</Badge>
            <span className="text-gray-500">Register no: <b className="text-gray-800">{query.data?.candidate.register_no ?? '—'}</b></span>
          </div>
          {movements.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">No passport collection or return recorded yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {movements.map((m) => (
                <li key={m.id} className="space-y-1 px-3 py-2.5 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium capitalize">{m.action === 'collect' ? 'Collected' : 'Given'} <span className="font-normal text-gray-500">· {m.reason}</span></span>
                    <StatusBadge status={m.status} />
                  </div>
                  {m.note && <div className="text-xs italic text-gray-500">"{m.note}"</div>}
                  <div className="text-xs text-gray-500">Requested by {m.requested_by} · {fmt(m.requested_at)}</div>
                  {m.status !== 'pending' && (
                    <div className="text-xs text-gray-500">
                      {m.status === 'approved' ? 'Approved' : 'Rejected'} by {m.reviewed_by || '—'} · {fmt(m.reviewed_at)}
                      {m.review_remarks && <span className="italic"> — "{m.review_remarks}"</span>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
